import { useState, useEffect } from "react";
import { useLocation, useRoute, Link } from "wouter";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Lock, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "../i18n";

export default function ResetPassword() {
  const { t, setLanguage } = useTranslation();
  const [, setLocation] = useLocation();
  const [, params] = useRoute("/reset-password");
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Get token and language from URL query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get("token");
    const langParam = urlParams.get("lang");
    
    if (tokenParam) {
      setToken(tokenParam);
    }
    
    // Set language if provided in URL
    if (langParam && (langParam === "de" || langParam === "en" || langParam === "hr")) {
      setLanguage(langParam);
    }
  }, [setLanguage]);

  const verifyToken = trpc.auth.verifyResetToken.useQuery(
    { token },
    {
      enabled: !!token,
      retry: false,
    }
  );

  const resetPassword = trpc.auth.resetPassword.useMutation({
    onSuccess: () => {
      setSuccess(true);
      toast.success(t.resetPassword.success);
      setTimeout(() => {
        setLocation("/login");
      }, 3000);
    },
    onError: (error) => {
      toast.error(error.message || t.auth.passwordResetSuccess);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error(t.resetPassword.passwordMismatch);
      return;
    }

    if (newPassword.length < 8) {
      toast.error(t.resetPassword.passwordTooShort);
      return;
    }

    resetPassword.mutate({ token, newPassword });
  };

  if (!token) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <XCircle className="h-16 w-16 text-destructive" />
            </div>
            <CardTitle>{t.resetPassword.invalidToken}</CardTitle>
            <CardDescription>
              {t.resetPassword.invalidTokenMessage}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link href="/forgot-password">
                {t.resetPassword.requestNew}
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (verifyToken.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardDescription>{t.resetPassword.verifying}</CardDescription>
          </CardHeader>
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (verifyToken.data && !verifyToken.data.valid) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <XCircle className="h-16 w-16 text-destructive" />
            </div>
            <CardTitle>{t.resetPassword.invalidToken}</CardTitle>
            <CardDescription>
              {t.resetPassword.invalidTokenMessage}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button asChild className="w-full">
              <Link href="/forgot-password">
                {t.resetPassword.requestNew}
              </Link>
            </Button>
            <Button asChild variant="outline" className="w-full">
              <Link href="/login">
                {t.forgotPassword.backToLogin}
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle className="h-16 w-16 text-green-500" />
            </div>
            <CardTitle>{t.resetPassword.success}</CardTitle>
            <CardDescription>
              {t.resetPassword.successMessage}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground text-center mb-4">
              {t.common.loading}
            </p>
            <Button asChild className="w-full">
              <Link href="/login">
                {t.auth.login}
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t.resetPassword.title}</CardTitle>
          <CardDescription>
            {t.resetPassword.subtitle}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="newPassword" className="text-sm font-medium">
                {t.resetPassword.newPassword}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="newPassword"
                  type="password"
                  placeholder={t.register.passwordPlaceholder}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pl-10"
                  required
                  minLength={8}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="text-sm font-medium">
                {t.resetPassword.confirmPassword}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder={t.register.confirmPasswordPlaceholder}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10"
                  required
                  minLength={8}
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={resetPassword.isPending}
            >
              {resetPassword.isPending ? t.common.loading : t.resetPassword.resetButton}
            </Button>

            <Button asChild variant="ghost" className="w-full">
              <Link href="/login">
                {t.forgotPassword.backToLogin}
              </Link>
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
